import React, { useState, useEffect, useMemo } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useQuery } from '@apollo/client';
import Fade from '@material-ui/core/Fade';
import { useParams } from 'react-router-dom';

import { Container } from '@Calix-Commerce/design-system/layout';
import { Tabs, Tab, TabLabel } from '@Calix-Commerce/design-system/components';

import { authenticationState, loadingState } from 'state/atoms';
import { GET_ORDER } from 'connectors/queries/order';
import { CurrencyContext } from 'utils/contexts';

import { DetailsBlock, ShippingBlock, SummaryTable, ItemDetailsTable } from './subcomponents';
import { Title } from './styledComponents';
import { parseOrderLines } from './helpers';
import { FsanDownloadButton } from './subcomponents/FsanDownloadButton';

// Tab identifiers
const SUMMARY_TAB = 'summary';
const ITEM_DETAILS_TAB = 'itemDetails';

const maxRetries = parseInt(process.env.REACT_APP_ORDER_DETAILS_MAX_FETCH_RETRIES);

const Order = () => {
  const [retriesLeft, setRetriesLeft] = useState(maxRetries);
  const [selectedTab, setSelectedTab] = useState(SUMMARY_TAB);

  const setLoading = useSetRecoilState(loadingState);
  const { accountId } = useRecoilValue(authenticationState);

  const { orderNumber } = useParams();
  const { data, error, loading, refetch } = useQuery(GET_ORDER, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      accountId,
      salesOrderNumber: orderNumber,
    },
    onCompleted: () => {
      setRetriesLeft(maxRetries);
      setLoading(false);
    },
    onError: () => {
      if (retriesLeft) {
        setRetriesLeft(retriesLeft - 1);
        refetch();
      } else {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    setLoading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderData = data && data.getAccount && data.getAccount.getOrder;
  const linesData = useMemo(() => parseOrderLines(orderData && orderData.lines), [orderData]);

  const renderOrderDetails = () => {
    return error ? (
      <span style={{ fontSize: '1.2rem' }}>
        The order for SO: {orderNumber} is not available yet. Please try again later.
      </span>
    ) : (
      <CurrencyContext.Provider value={orderData.currencyCode}>
        <DetailsBlock orderData={orderData} />
        <ShippingBlock shippingData={orderData.shippingInfo} billingData={orderData.billingInfo} />
        <FsanDownloadButton orderData={orderData} />
        <Tabs>
          <Tab selected={selectedTab === SUMMARY_TAB} onClick={() => setSelectedTab(SUMMARY_TAB)}>
            <TabLabel>Summary</TabLabel>
          </Tab>
          <Tab
            selected={selectedTab === ITEM_DETAILS_TAB}
            onClick={() => setSelectedTab(ITEM_DETAILS_TAB)}
          >
            <TabLabel>Item Details</TabLabel>
          </Tab>
        </Tabs>
        {selectedTab === SUMMARY_TAB ? (
          <Fade in timeout={500}>
            <div>
              <SummaryTable linesData={linesData} />
            </div>
          </Fade>
        ) : null}
        {selectedTab === ITEM_DETAILS_TAB ? (
          <Fade in timeout={500}>
            <div>
              <ItemDetailsTable linesData={linesData} />
            </div>
          </Fade>
        ) : null}
      </CurrencyContext.Provider>
    );
  };

  return loading ? null : (
    <Container>
      <Title>Order Details</Title>
      {renderOrderDetails()}
    </Container>
  );
};

export { Order };
